import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SectionHeading from "../components/SectionHeading";
import { products } from "../config/products";
import "./ProductTemplate.css";

function ProductTemplate() {
  const { slug } = useParams();
  const [currency, setCurrency] = useState("EUR");
  const product = products.find((p) => p.slug === slug);

  if (!product) {
    return (
      <div className="product-template">
        <div className="container">
          <div className="prot-not-found">
            <h1>Product Not Found</h1>
            <Link to="/products">Back to Products</Link>
          </div>
        </div>
      </div>
    );
  }

  const priceEUR = product.priceEUR || 0;
  const priceUSD = product.priceUSD || Math.round(priceEUR * 1.1);
  const price = currency === "USD" ? priceUSD : priceEUR;
  const symbol = currency === "USD" ? "$" : "€";
  const delivery =
    product.delivery ||
    (priceUSD === 0 ? "free" : priceUSD > 500 ? "contact-sales" : "auto");

  return (
    <>
      <Helmet>
        <title>
          {product.name} — {product.tagline} | AxiomLabs Solutions
        </title>
        <meta name="description" content={product.description} />
      </Helmet>
      <div className="product-template">
        {/* Hero */}
        <section className="prot-hero">
          <div className="container">
            <Link to="/products" className="prot-back">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
              Back to Products
            </Link>

            <div className="prot-hero__inner">
              <div className="prot-hero__content">
                <span className="prot-eyebrow">{product.tagline}</span>
                <h1 className="prot-title">{product.name}</h1>
                <p className="prot-subtitle">{product.description}</p>
                <div className="prot-badges">
                  <span className="prot-badge prot-badge--available">
                    {product.status === "Available"
                      ? "Available Now"
                      : product.status}
                  </span>
                  {product.badge && (
                    <span className="prot-badge prot-badge--featured">
                      {product.badge}
                    </span>
                  )}
                  <span className="prot-badge prot-badge--license">
                    {product.license}
                  </span>
                </div>
              </div>
              <div className="prot-hero__image">
                <div className="prot-image-placeholder">
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1"
                    strokeLinecap="round"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                  <span>Screenshot</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        {product.features && (
          <section className="prot-features">
            <div className="container">
              <SectionHeading
                eyebrow="Key Features"
                title={product.featuresTitle || "What It Does"}
                subheading={product.featuresSubtitle || ""}
              />
              <div className="prot-features__grid">
                {product.features.map((f, i) => (
                  <div key={i} className="prot-feature-card">
                    <span className="prot-feature-card__icon">◆</span>
                    <h3 className="prot-feature-card__title">{f.title}</h3>
                    <p className="prot-feature-card__text">{f.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Pricing or Free Download */}
        <section className="prot-pricing">
          <div className="container">
            {delivery === "free" ? (
              <>
                <SectionHeading
                  eyebrow="Free Software"
                  title="Free Forever."
                  subheading="No sign-up. No credit card. Just download and use."
                />
                <div className="prot-pricing__card">
                  <span className="prot-pricing__label">Open Source</span>
                  <span className="prot-pricing__price">Free</span>
                  <span className="prot-pricing__period">
                    {product.name} — {product.license}
                  </span>
                  <ul className="prot-pricing__features">
                    {product.checkoutFeatures?.map((f, i) => (
                      <li key={i}>{f}</li>
                    ))}
                  </ul>
                  {product.downloadUrl ? (
                    <Link
                      to={product.downloadUrl}
                      className="prot-pricing__button"
                    >
                      <span>Get Started</span>
                      <span className="prot-pricing__arrow">→</span>
                    </Link>
                  ) : (
                    <Link
                      to={`/success?product=${product.slug}&free=true`}
                      className="prot-pricing__button"
                    >
                      <span>Download Free</span>
                      <span className="prot-pricing__arrow">→</span>
                    </Link>
                  )}

                </div>
              </>
            ) : delivery === "contact-sales" ? (
              <>
                <SectionHeading
                  eyebrow="Simple Pricing"
                  title="One Price. Own It Forever."
                  subheading="No subscriptions. No tiers. No hidden fees."
                />
                <div className="prot-pricing__card">
                  <span className="prot-pricing__label">
                    Enterprise License
                  </span>
                  <div className="prot-currency">
                    <button
                      className={`prot-currency__btn ${currency === "EUR" ? "prot-currency__btn--active" : ""}`}
                      onClick={() => setCurrency("EUR")}
                    >
                      € EUR
                    </button>
                    <button
                      className={`prot-currency__btn ${currency === "USD" ? "prot-currency__btn--active" : ""}`}
                      onClick={() => setCurrency("USD")}
                    >
                      $ USD
                    </button>
                  </div>
                  <span className="prot-pricing__price">
                    {symbol}
                    {price.toLocaleString()}
                  </span>
                  <span className="prot-pricing__period">
                    {product.name} license
                  </span>
                  <ul className="prot-pricing__features">
                    {product.checkoutFeatures?.map((f, i) => (
                      <li key={i}>{f}</li>
                    ))}
                  </ul>
                  <a
                    href={`mailto:axiomlabssolutions@gmail.com?subject=Enterprise Inquiry: ${encodeURIComponent(product.name)}`}
                    className="prot-pricing__button"
                  >
                    <span>Contact Sales</span>
                    <span className="prot-pricing__arrow">→</span>
                  </a>
                </div>
              </>
            ) : (
              <>
                <SectionHeading
                  eyebrow="Simple Pricing"
                  title="One Price. Own It Forever."
                  subheading="No subscriptions. No tiers. No hidden fees."
                />
                <div className="prot-pricing__card">
                  <span className="prot-pricing__label">One-Time Purchase</span>
                  <div className="prot-currency">
                    <button
                      className={`prot-currency__btn ${currency === "EUR" ? "prot-currency__btn--active" : ""}`}
                      onClick={() => setCurrency("EUR")}
                    >
                      € EUR
                    </button>
                    <button
                      className={`prot-currency__btn ${currency === "USD" ? "prot-currency__btn--active" : ""}`}
                      onClick={() => setCurrency("USD")}
                    >
                      $ USD
                    </button>
                  </div>
                  <span className="prot-pricing__price">
                    {symbol}
                    {price.toLocaleString()}
                  </span>
                  <span className="prot-pricing__period">
                    {product.name} license
                  </span>
                  <ul className="prot-pricing__features">
                    {product.checkoutFeatures?.map((f, i) => (
                      <li key={i}>{f}</li>
                    ))}
                  </ul>
                  <Link
                    to={`/checkout?currency=${currency}&product=${product.slug}`}
                    className="prot-pricing__button"
                  >
                    <span>Purchase Now</span>
                    <span className="prot-pricing__arrow">→</span>
                  </Link>
                </div>
              </>
            )}
          </div>
        </section>

        {/* Sample Gallery (for products with images) */}
        {product.sampleImages && product.sampleImages.length > 0 && (
          <section className="prot-samples">
            <div className="container">
              <SectionHeading
                eyebrow="Sample Outputs"
                title="What It Creates"
                subheading="Real outputs generated with this tool — no editing or post-processing."
              />
              <div className="prot-samples__grid">
                {product.sampleImages.map((img, i) => (
                  <div key={i} className="prot-samples__item">
                    <img src={img.src} alt={img.alt} loading="lazy" />
                    <span className="prot-samples__caption">{img.alt}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
}

export default ProductTemplate;
